import { usePackageRestrictionContext } from "~/features/PackageRestriction/PackageRestrictionContext";
import { useSalesData } from "~/features/salesCampaign/hooks/useSalesData";
import { PackagingSelectionVariant } from "~/domains/product/components/PackagingSelection/PackagingSelectionVariant";
import { useDispatch } from "react-redux";
import { useMemo } from "react";
import { addMessage } from "~/features/toasts/redux/actions";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

interface Props {
  product: ProductDescriptor;
  variant: PackagingSelectionVariant;
}

export const usePackagingSelectionLogic = ({ product, variant }: Props) => {
  const dispatch = useDispatch();

  const restrictedCode = usePackageRestrictionContext();
  const salesData = useSalesData();

  return useMemo(() => {
    const packagingOptions = product?.availability?.packagingInformation ?? null;

    if (!packagingOptions || !product.isLoaded) {
      return null;
    }

    let options = packagingOptions;
    if (restrictedCode) {
      options = options.filter((option) => restrictedCode === option.packCode);
    }

    if (options.length === 0) {
      dispatch(addMessage("info", "restrictedPackaging.notFound", "translation"));
      return packagingOptions;
    }

    if (variant === PackagingSelectionVariant.Campaign) {
      const validPackageCodes = new Set(Object.keys(salesData?.salePerPack ?? {}));
      options = options.filter((option) => validPackageCodes.has(option.packCode));
    }

    return options;
  }, [
    [product.isAvailabilityLoading, restrictedCode, variant, salesData, product?.availability?.packagingInformation],
  ]);
};
